<template>
  <div class="license-info">
    <form-license-period
      :showing="showModal"
      :license-period="newLicensePeriodTemplate"
      :renew-mode="true"
      :disable-properties="['publisher_id']"
      @refresh="refresh()"
      @close="closeFormModal()"
    />
    <div class="card-wrapper">
      <div class="content row">
        <div class="col-xs-6">
          <div class="row">
            <div class="column col-xs-3 q-pr-md">{{ getTranslation('licenses.publisher') || 'Publisher' }}:</div>
            <div class="col-xs-6 q-pl-md text-right">
              <entity-editor
                :params="licenseModel"
                :options="publisherOptions"
                :value="licenseModel.publisher ? licenseModel.publisher.id : null"
                type="select"
                title="publisher"
                @update="saveEdit({ publisher_id: $event })"
              >
                <template slot="content">
                  <router-link
                    v-if="licenseModel.publisher.id && licenseModel.publisher.name"
                    :to="{
                      name: 'publisher',
                      params: { id: licenseModel.publisher.id }
                    }"
                    class="lm-link lm-link--tertiary"
                  >
                    {{ licenseModel.publisher.name }}
                  </router-link>
                </template>
              </entity-editor>
            </div>
          </div>
          <div class="row">
            <div class="column col-xs-3 q-pr-md">{{ getTranslation('licenses.managed_by') || 'Managed by' }}:</div>
            <div class="col-xs-6 q-pl-md text-right">
              <entity-editor
                :params="licenseModel"
                :options="selectOwners"
                :value="licenseModel.owner ? licenseModel.owner.id : null"
                type="select"
                title="managed by"
                @update="saveEdit({ owner_id: $event })"
              >
                <template slot="content">
                  {{ licenseModel.owner.name }}
                </template>
              </entity-editor>
            </div>
          </div>
        </div>
        <div class="col-xs-6">
          <div class="actions">
            <q-btn
              size="sm"
              color="tertiary"
              class="q-mr-sm"
              label="Create new licence period"
              @click="showFormModal()"
            />
            <q-btn round size="7px" icon="fas fa-cog" class="text-white" style="background: #1b4a6c">
              <q-menu fit anchor="bottom middle" self="top right">
                <q-list style="min-width: 150px;">
                  <q-item
                    v-for="(action, key) in topBarActions"
                    :key="key"
                    v-close-popup
                    :disable="action.disabled"
                    :class="action.textClass"
                    clickable
                    @click="performAction(action)"
                  >
                    <q-item-section>
                      <q-item-label>{{ action.name }}</q-item-label>
                      <q-item-label caption>{{ action.description }}</q-item-label>
                    </q-item-section>
                  </q-item>
                </q-list>
              </q-menu>
            </q-btn>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import EntityEditor from '@components/editors/EntityEditor'
import FormLicensePeriod from '@components/forms/form-license-period/FormLicensePeriod'
import { cloneDeep, get } from 'lodash'
import { mapState } from 'vuex'

export default {
  name: 'LmLicenseInfo',
  components: {
    EntityEditor,
    FormLicensePeriod
  },
  props: {
    license: {
      type: Object,
      required: true
    },
    topBarActions: Object,
    publishers: Array
  },
  data() {
    return {
      licenseModel: null,
      latestLicensePeriod: null,
      showModal: false
    }
  },
  computed: {
    ...mapState({
      editingData: state => state.data.inlineEditingData,
      actions: state => state.licensePeriods.actions
    }),
    selectOwners() {
      if (!this.editingData || !this.editingData.licenses) {
        return
      }
      return this.generateOptions({
        collection: this.editingData.licenses.owners
      })
    },
    publisherOptions() {
      return this.generateOptions({ collection: this.publishers })
    },
    newLicensePeriodTemplate() {
      if (!this.latestLicensePeriod) {
        return
      }
      let license = cloneDeep(this.license)
      return this.latestLicensePeriod
        ? {
            id: null,
            license_id: license.id,
            publisher_id: license.publisher.id,
            name: license.name,
            start_date: this.latestLicensePeriod.start_date,
            end_date: this.latestLicensePeriod.end_date,
            summary: this.latestLicensePeriod.summary,
            subscriptions: this.latestLicensePeriod.subscriptions,
            owner_id: license.owner.id,
            license_file: this.latestLicensePeriod.license_file,
            terms: this.latestLicensePeriod.terms,
            status: null,
            actions: this.actions
          }
        : null
    }
  },

  watch: {
    license: {
      handler() {
        this.licenseModel = cloneDeep(this.license)
      },
      deep: true,
      immediate: true
    }
  },

  methods: {
    saveEdit(event) {
      return this.$store
        .dispatch('licenses/update', {
          id: this.license.id,
          value: event
        })
        .then(result => {
          if (!get(result, 'errors')) {
            let data = cloneDeep(this.licenseModel)
            this.$set(this, 'licenseModel', Object.assign(data, result))
            this.$emit('refresh')
          }
        })
    },
    showFormModal() {
      this.$store
        .dispatch('licensePeriods/getSingle', {
          id: this.license.last_period_id
        })
        .then(response => {
          this.$set(this, 'latestLicensePeriod', response)
          this.showModal = true
        })
    },
    closeFormModal() {
      this.showModal = false
    },
    refresh() {
      this.closeFormModal()
      return this.$store.dispatch('licenses/getSingle', {
        id: this.license.id
      })
    },
    performAction(params) {
      this.$store.dispatch(params.action, params)
    }
  }
}
</script>

<style lang="stylus" scoped>
.license-info {
  background #E6E6E6
  padding 20px
  font-size 12px
  box-shadow 0 2px 0 #bdc6cc

  .header {
    display grid
    grid-template-columns 150px auto
    justify-content space-between
    align-items center

    .header-title {
      font-size 18px
      font-weight 500
      color #00
    }

    .actions {
      display grid
      grid-auto-flow column
      grid-gap 10px
      align-items center
    }
  }
  .content {
    .actions {
      display flex
      justify-content flex-end
      align-items baseline
    }
  }
  .card-wrapper {
    background white
    padding 15px
  }

  >>> .editable-content .editable-content__text {
    width 100%
  }

}
</style>
